import React, {useState} from "react";
import {InfoSignIcon, Tooltip} from "evergreen-ui";

const BAND_TOTAL = "#cfe6f8";
const BAND_TOTAL_BORDER = "#a9d2f0";
const BAND_MONTH = "#e6f1fb";

export function calcUtilizationPct(billed: number, required: number): number {
    if (!required) return 0;
    return Math.min(100, Math.round((billed / required) * 100));
}

function formatHours(value: number): string {
    if (Number.isInteger(value)) return String(value);
    return value.toFixed(2);
}

export function formatUtilizationPct(billed: number, required: number): string {
    if (!required) return "-";
    return `${calcUtilizationPct(billed, required)}%`;
}

export type UtilizationHoursValues = {
    authorizedHours: number;
    directCareHoursRequired: number;
    utilizedHours: number;
};

export type UtilizationHoursRow = UtilizationHoursValues & {
    procedureCode: string;
    testId: string;
};

export type UtilizationHoursTotalRow = UtilizationHoursValues & {
    label: string;
    testId: string;
};

export type UtilizationHoursGroup = UtilizationHoursValues & {
    key: string;
    label: string;
    testId: string;
    ariaLabel: string;
    rows: UtilizationHoursRow[];
};

type Props = {
    firstColumnLabel: string;
    ariaLabel: string;
    role?: "treegrid";
    totalRow?: UtilizationHoursTotalRow;
    rows?: UtilizationHoursRow[];
    groups?: UtilizationHoursGroup[];
};

export function UtilizationHoursTable({
    firstColumnLabel,
    ariaLabel,
    role,
    totalRow,
    rows = [],
    groups = [],
}: Props) {
    const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

    const toggleGroup = (key: string) => {
        setExpandedGroups((prev) => ({...prev, [key]: !prev[key]}));
    };

    return (
        <table className="w-full table-fixed text-sm tabular-nums" role={role} aria-label={ariaLabel}>
            <colgroup>
                <col style={{width: "40%"}}/>
                <col style={{width: "12%"}}/>
                <col style={{width: "20%"}}/>
                <col style={{width: "12%"}}/>
                <col style={{width: "16%"}}/>
            </colgroup>
            <ColumnHeaders firstColumn={firstColumnLabel}/>
            <tbody>
                {totalRow && (
                    <tr data-testid={totalRow.testId} style={{backgroundColor: BAND_TOTAL}}>
                        <td className="text-left font-bold py-3 px-3" style={{borderTop: `2px solid ${BAND_TOTAL_BORDER}`, borderBottom: `2px solid ${BAND_TOTAL_BORDER}`}}>
                            {totalRow.label}
                        </td>
                        <HoursCell value={totalRow.authorizedHours} strong borderColor={BAND_TOTAL_BORDER}/>
                        <HoursCell value={totalRow.directCareHoursRequired} strong borderColor={BAND_TOTAL_BORDER}/>
                        <HoursCell value={totalRow.utilizedHours} strong borderColor={BAND_TOTAL_BORDER}/>
                        <td className="text-right font-bold py-3 px-3" style={{borderTop: `2px solid ${BAND_TOTAL_BORDER}`, borderBottom: `2px solid ${BAND_TOTAL_BORDER}`}}>
                            {formatUtilizationPct(totalRow.utilizedHours, totalRow.directCareHoursRequired)}
                        </td>
                    </tr>
                )}
                {rows.map((row) => (
                    <HoursRow key={row.testId} row={row}/>
                ))}
                {groups.map((group) => {
                    const expanded = !!expandedGroups[group.key];
                    return (
                        <React.Fragment key={group.key}>
                            <tr
                                data-testid={group.testId}
                                role="row"
                                tabIndex={0}
                                aria-expanded={expanded}
                                aria-label={group.ariaLabel}
                                className="font-semibold cursor-pointer"
                                onClick={() => toggleGroup(group.key)}
                                onKeyDown={(event) => {
                                    if (event.key === "Enter" || event.key === " ") {
                                        event.preventDefault();
                                        toggleGroup(group.key);
                                    }
                                }}
                            >
                                <td className="text-left py-3 px-3 border-b border-gray-200" style={{backgroundColor: BAND_MONTH}}>
                                    <span
                                        aria-hidden="true"
                                        className="inline-flex items-center justify-center mr-2"
                                        style={{
                                            width: 18,
                                            height: 18,
                                            fontSize: 11,
                                            transform: expanded ? "rotate(90deg)" : undefined,
                                            transition: "transform .15s ease",
                                        }}
                                    >
                                        ▶
                                    </span>
                                    <span className="font-bold">{group.label}</span>
                                </td>
                                <td className="text-right py-3 px-3 border-b border-gray-200" style={{backgroundColor: BAND_MONTH}}>
                                    {formatHours(group.authorizedHours)}
                                </td>
                                <td className="text-right py-3 px-3 border-b border-gray-200" style={{backgroundColor: BAND_MONTH}}>
                                    {formatHours(group.directCareHoursRequired)}
                                </td>
                                <td className="text-right py-3 px-3 border-b border-gray-200" style={{backgroundColor: BAND_MONTH}}>
                                    {formatHours(group.utilizedHours)}
                                </td>
                                <td className="text-right py-3 px-3 border-b border-gray-200 font-semibold" style={{backgroundColor: BAND_MONTH}}>
                                    {formatUtilizationPct(group.utilizedHours, group.directCareHoursRequired)}
                                </td>
                            </tr>
                            {expanded && group.rows.map((row) => (
                                <HoursRow key={row.testId} row={row} indented/>
                            ))}
                        </React.Fragment>
                    );
                })}
            </tbody>
        </table>
    );
}

function HoursRow({row, indented}: {row: UtilizationHoursRow; indented?: boolean}) {
    return (
        <tr data-testid={row.testId}>
            <td className="text-left py-3 px-3 border-b border-gray-200" style={indented ? {paddingLeft: 32} : undefined}>
                <CodeLabel code={row.procedureCode}/>
            </td>
            <NumericCells
                authorizedHours={row.authorizedHours}
                directCareHoursRequired={row.directCareHoursRequired}
                utilizedHours={row.utilizedHours}
            />
        </tr>
    );
}

function ColumnHeaders({firstColumn}: {firstColumn: string}) {
    const headerClass = "text-xs font-bold uppercase tracking-wide text-gray-600 py-3 px-3 border-b-2 border-gray-300 bg-white";
    return (
        <thead>
            <tr>
                <th className={`text-left ${headerClass}`}>{firstColumn}</th>
                <th className={`text-right ${headerClass}`}>
                    Authorized
                    <span className="block font-normal normal-case tracking-normal text-gray-500" style={{fontSize: 11}}>hrs</span>
                </th>
                <th className={`text-right ${headerClass}`}>
                    Min. Direct Required
                    <span className="block font-normal normal-case tracking-normal text-gray-500" style={{fontSize: 11}}>hrs</span>
                </th>
                <th className={`text-right ${headerClass}`}>
                    Utilized
                    <span className="block font-normal normal-case tracking-normal text-gray-500" style={{fontSize: 11}}>hrs</span>
                </th>
                <th className={`text-right ${headerClass}`}>
                    <span className="inline-flex items-center justify-end">
                        Utilization
                        <Tooltip content="Utilization is calculated as Utilized Hours / Minimum Direct Hours Required. The displayed utilization percentage is capped at 100%."
                                 appearance="card">
                            <span className="ml-1 inline-flex items-center" data-testid="utilization-pct-info">
                                <InfoSignIcon size={16}/>
                            </span>
                        </Tooltip>
                    </span>
                    <span className="block font-normal normal-case tracking-normal text-gray-500" style={{fontSize: 11}}>% of authorized</span>
                </th>
            </tr>
        </thead>
    );
}

function CodeLabel({code}: {code: string}) {
    return (
        <span className="inline-block font-bold text-gray-900 bg-gray-100 border border-gray-300 rounded px-2 py-px text-sm">
            {code}
        </span>
    );
}

function NumericCells({
    authorizedHours,
    directCareHoursRequired,
    utilizedHours,
}: UtilizationHoursValues) {
    return (
        <>
            <td className="text-right py-3 px-3 border-b border-gray-200">{formatHours(authorizedHours)}</td>
            <td className="text-right py-3 px-3 border-b border-gray-200">{formatHours(directCareHoursRequired)}</td>
            <td className="text-right py-3 px-3 border-b border-gray-200">{formatHours(utilizedHours)}</td>
            <td className="text-right py-3 px-3 border-b border-gray-200 font-semibold">
                {formatUtilizationPct(utilizedHours, directCareHoursRequired)}
            </td>
        </>
    );
}

function HoursCell({value, strong, borderColor}: {value: number; strong?: boolean; borderColor: string}) {
    return (
        <td
            className={`text-right py-3 px-3 ${strong ? "font-bold" : ""}`}
            style={{borderTop: `2px solid ${borderColor}`, borderBottom: `2px solid ${borderColor}`}}
        >
            {formatHours(value)}
        </td>
    );
}
